<template>
  <div>
    <div class="button cta success" @click="showAddCardModal = true">Add Credit Card</div>

    <table>
      <thead>
        <th>{{ $t('consumer.creditCards.addedOn') }}</th>
        <th>{{ $t('consumer.creditCards.creditCard') }}</th>
        <th>{{ $t('consumer.creditCards.address') }}</th>
        <th>{{ $t('consumer.creditCards.city') }}</th>
        <th>{{ $t('consumer.creditCards.state') }}</th>
        <th>{{ $t('consumer.creditCards.zip') }}</th>
        <th>{{ $t('consumer.creditCards.status') }}</th>
      </thead>
      <tbody>
        <tr v-for="card in creditCards" v-show="creditCards.length > 0" :key="card.id">
          <td>{{ card.date }}</td>
          <td>{{ card.card }}</td>
          <td>{{ card.address }}</td>
          <td>{{ card.city }}</td>
          <td>{{ card.state }}</td>
          <td>{{ card.zip }}</td>
          <td v-bind:class="['status', cardStatus(card.status)]">{{ cardStatus(card.status) }}</td>
        </tr>

        <tr v-show="loading">
          <td colspan="7" class="table-block">
              <i class="fa fa-circle-notch fa-spin"></i>
              {{ $t('consumer.creditCards.loading') }}
          </td>
        </tr>

        <tr v-show="creditCards.length === 0 && !loading">
          <td colspan="7" class="table-block">
            {{ $t('consumer.creditCards.emptyState') }}
          </td>
        </tr>
      </tbody>
    </table>

    <div class="cards-mobile">
      <div v-show="loading">
        <i class="fa fa-circle-notch fa-spin"></i>
        {{ $t('consumer.creditCards.loading') }}
      </div>
      <div v-show="creditCards.length === 0 && !loading">
         {{ $t('consumer.creditCards.emptyState') }}
      </div>
      <div class="card" v-for="card in creditCards" v-show="creditCards.length > 0" :key="card.id">
        <div class="title">
          <div class="label">Added On: </div>
          <div class="value">{{ card.date }}</div>
        </div>
        <div class="title">
          <div class="label">Card #: </div>
          <div class="value">{{ card.card }}</div>
        </div>
        <div class="title">
          <div class="label">Address: </div>
          <div class="value">{{ card.address }}</div>
        </div>
        <div class="title">
          <div class="label">City: </div>
          <div class="value">{{ card.city }}</div>
        </div>
        <div class="title">
          <div class="label">State: </div>
          <div class="value">{{ card.state }}</div>
        </div>
        <div class="title">
          <div class="label">Zip: </div>
          <div class="value">{{ card.zip }}</div>
        </div>
        <div class="title">
          <div class="label">Status: </div>
          <div v-bind:class="['value status', cardStatus(card.status)]">{{ cardStatus(card.status) }}</div>
        </div>
      </div>
    </div>

    <transition name="expand">
      <div class="respond-box" v-if="showAddCardModal">
        <div class="vertical-center">
          <div class="card add-credit-box">
            <div class="heading">ADD CREDIT CARD</div>
            <div class="row">
              <div class="medium-12 column">
                <div class="data-field">
                  <div class="label">Credit Card #</div>
                  <div class="credit-card-input">
                    <input type="text" class="input" :disabled="loading"
                      v-model="creditCardData.card" @keyup="cardTypeCheck" />
                    <span :class="['cc-icon', cardType]"></span>
                  </div>
                  <span class="error-msg">{{ validation.card }}</span>
                </div>
              </div>
              
              <div class="medium-12 column">
                <div class="data-field">
                  <div class="label">Cardholder Name</div>
                  <input type="text" class="input" :disabled="loading"
                    v-model="creditCardData.owner" />
                  <span class="error-msg">{{ validation.owner }}</span>
                </div>
              </div>

              <div class="medium-6 column">
                <div class="data-field">
                  <div class="label">EXPIRATION (MM/YYYY)</div>
                  <input type="text" class="input" :disabled="loading"
                    v-model="creditCardData.expiration"
                    v-on:keyup="formatExpCC" v-on:keypress="isNumber" />
                  <span class="error-msg">{{ validation.expiration }}</span>
                </div>
              </div>
              <div class="medium-6 column">
                <div class="data-field">
                  <div class="label">CVV</div>
                  <input type="text" class="input" :disabled="loading"
                    v-model="creditCardData.cvv" v-on:keypress="isNumber" />
                  <span class="error-msg">{{ validation.cvv }}</span>
                </div>
              </div>

              <div class="medium-12 column">
                <div class="data-field">
                  <div class="label">Address1</div>
                  <input type="text" class="input" :disabled="loading"
                    v-model="creditCardData.address" />
                  <span class="error-msg">{{ validation.address }}</span>
                </div>
              </div>

              <div class="medium-12 column margin-B-s">
                <div class="data-field">
                  <div class="label">City</div>
                  <input type="text" class="input" :disabled="loading"
                    v-model="creditCardData.city" />
                  <span class="error-msg">{{ validation.city }}</span>
                </div>
              </div>

              <div class="medium-6 columns margin-B-s">
                <div class="data-field">
                  <div class="label">State</div>
                  <select v-model="creditCardData.state" :disabled="loading"
                    :class="{'has-error':false}">
                    <option value=""></option>
                    <option value="NonUS">Non US</option>
                    <option v-for="state in states" :value="state.label" :key="state.value">{{state.label}}</option>
                  </select>
                  <span class="error-msg">{{ validation.state }}</span>
                </div>
              </div>

              <div class="medium-6 column margin-B-s">
                <div class="data-field">
                  <div class="label">Zip</div>
                  <input type="text" class="input" :disabled="loading"
                    v-model="creditCardData.zip" />
                  <span class="error-msg">{{ validation.zip }}</span>
                </div>
              </div>

              <div class="medium-12 column margin-B-s" v-show="creditCardData.state === 'NonUS'">
                <div class="data-field">
                  <div class="label">Country</div>
                  <select v-model="creditCardData.country" :disabled="loading"
                    :class="{'input':true, 'has-error':false}">
                    <option v-for="(country, index) in countries.all"
                      :key="index" :value="country.alpha2">{{country.name}}</option>
                  </select>
                  <span class="error-msg">{{ validation.country }}</span>
                </div>
              </div>
            </div>

            <div class="button cta block small" @click="addCard">
              <span v-if="loading">
                <i class="fa fa-spinner fa-spin"></i>
                Loading ...
              </span>
              <span v-else>Submit</span>
            </div>
            <div class="close" @click="showAddCardModal = false">Close</div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="expand">
      <div class="respond-box" v-if="respond.show">
        <div class="vertical-center">
          <div class="card ">
            <div class="success">
              <i class="fas fa-check-circle" v-if="respond.success"></i>
              <i class="fas fa-times-circle" v-else></i>
              <div class="label">{{ respond.label }}</div>
              <div class="message" v-if="!!respond.message" v-html="respond.message"></div>
            </div>
            <div class="button cta block small margin-T-l" @click="closeRespondModal">Close</div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ApiCards from '@/modules/api/consumer/credit-cards'
import US from 'us'
import creditCardType, { types as CardType } from 'credit-card-type';

export default {
  name: 'credit-card',
  data () {
    return {
      showAddCardModal: false,
      respond: {
        show: false,
        success: false,
        label: '',
        message: ''
      },
      cardType: 'unknown',
      countries: require('country-data').countries,
      validation: {
        owner: '',
        card: '',
        expiration: '',
        cvv: '',
        address: '',
        city: '',
        state: '',
        country: ''
      },
      loading: false
    }
  },
  computed: {
    creditCardData () { return this.$store.state.corporate.creditCard },
    conId () { return this.$store.state.corporate.profile.info.consId },
    creditCards () { return this.$store.state.consumer.creditCards.list },
    conId () { return this.$store.state.corporate.profile.info.consId },
    states () {
      let states = []
      for (let key in US.states) {
        if (US.states.hasOwnProperty(key)) {
          states.push({
            value: key,
            label: US.states[key].name
          })
        }
      }
      return states
    }
  },
  methods: {
    init () {
      this.$store.commit('consumer/resetCards')
      this.$store.commit('consumer/setCardsIsMax', false)
      this.$store.commit('consumer/setCardsCurrentPage', 0)

      this.getCards()
    },
    getCards () {
      let self = this
      if (self.loading || !self.conId) { return }

      self.loading = true
      ApiCards.getCards(self, self.conId, function () {
        self.loading = false
      })
    },
    cardStatus (isVerified) { return isVerified ? 'verified' : 'unverified' },
    cardTypeCheck () {
      if (this.creditCardData.card === '') {
        this.cardType = 'unknown'
        return
      }

      let possibleCards = creditCardType(this.creditCardData.card);
      this.cardType = 'unknown'

      for (let i = 0; i < possibleCards.length; i++) {
        let possibleCard = possibleCards[i]
        let cardType = possibleCard.type

        if (cardType === CardType.VISA) {
          this.cardType = 'visa'
        } else if (cardType === CardType.MASTERCARD) {
          this.cardType = 'mastercard'
        } else if (cardType === CardType.AMERICAN_EXPRESS) {
          this.cardType = 'amex'
        } else if (cardType === CardType.DINERS_CLUB) {
          this.cardType = 'dinersclub'
        } else if (cardType === CardType.DISCOVER) {
          this.cardType = 'discover'
        } else if (cardType === CardType.JCB) {
          this.cardType = 'jcb'
        }
      }
    },
    isNumber (evt) {
      let charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
        evt.preventDefault()
        return
      }
      return true;
    },
    formatExpCC () {
      let cardExp = this.creditCardData.expiration
      if (cardExp.length === 2 && !isNaN(cardExp)) {
        this.creditCardData.expiration = cardExp + '/'
      }
    },
    addCard () {
      let self = this
      if (self.loading) { return }

      let hasError = false
      for (let field in this.validation) {
        this.validation[field] = ''
        if (this.creditCardData[field] === '') {
          this.validation[field] = 'This field cannot be empty.'
          hasError = true
        }
      }

      if (hasError) { return }

      self.loading = true
      self.$sdk.post('/consumer/' + self.conId + '/cards', {
        token: self.$store.state.session.sessionToken,
        title: '',
        name: self.creditCardData.owner,
        number: self.creditCardData.card,
        expiration: self.creditCardData.expiration,
        cvv: self.creditCardData.cvv,
        billingAddress: self.creditCardData.address,
        billingCity: self.creditCardData.city,
        billingState: self.creditCardData.state,
        billingCountry: self.creditCardData.country,
        billingZip: self.creditCardData.zip,
      }, res => {
        self.loading = false
        self.showAddCardModal = false
        if (!res.error) {
          self.respond = {
            show: true,
            success: true,
            label: 'Your credit card is being processed and will be added in a few moments.',
            message: ''
          }
        } else {
          self.respond = {
            show: true,
            success: false,
            label: 'Oops! Something went wrong.',
            message: res.error.message
          }
        }
      })
    },
    closeRespondModal () {
      this.respond.show = false
      if (this.respond.success) {
        this.getCards()
      }
    }
  },
  watch: {
    conId: function (value) {
      this.init()
    }
  },
  mounted () {
    this.init()
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/_creditCardIcons';

.respond-box{
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 4;
  background: rgba(0,0,0, .5);
  display: table;

  .vertical-center{
    display: table-cell;
    vertical-align: middle;
  }
  .card{
    width: 80%;
    max-width: 500px;
    margin: 0 auto;
    padding: 15px 25px;
    text-align: center;
  }
  .fa-check-circle, .fa-times-circle{
    font-size: 100px;
    color: #26d97a;
    margin-bottom: 5px;
  }
  .fa-times-circle{ color: #F20D0D; }
  .label{
    font-weight: 600;
    color: #505050;
  }
  .success .label{
    margin-bottom: 20px;
    padding: 0 40px;
  }
  .message{
    font-size: 14px;
    color: #777;
    margin: 10px 0;
  }
}

.add-credit-box{
  .heading{
    font-size: 21px;
    margin-bottom: 20px;
    font-weight: 600;
    color: #606060;
  }
  .label, .error-msg{ text-align: left; }
  .credit-card-input{
    position: relative;

    .cc-icon{
      position: absolute;
      top: 7px;
      left: 5px;
    }
    input{ padding-left: 43px; }
  }
  .button{ margin: 20px 0 10px; }
  .close{
    font-size: 14px;
    color: #888;
    cursor: pointer;
  }
}

.data-field{
  padding: 5px;
  position: relative;

  .label{
    color: #888;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    line-height: 130%;

    &.has-error{ color: #f20d0d; }
  }
  .input{
    margin: 1px 0 0;
    padding: 5px;
    font-size: 14px;
    color: #333;
    line-height: inherit;
    transition: all .35s ease;

    &.has-error{ border-color: #f20d0d; }
    &:disabled{ background: #eee; }
  }
  .error-msg{
    font-size: 11px;
    line-height: 1.6;
    color: #f20d0d;
    font-style: italic;
    display: block;
  }
  select{
    border: 1px solid #ddd;
    padding: 5px;
    color: #333;
    height: 35px;
    position: relative;
    font-size:14px;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url('/assets/img/dropdown-arrow.png');
    background-repeat: no-repeat;
    background-position: right 50%;
    transition: all .35s ease;

    &.has-error{ border-color: #f20d0d; }
    &:disabled{ background-color: #eee; }
  }
}

table{
  border-collapse: collapse;
  width: 100%;
  margin-top: 20px;

  thead{
    color: #8DA0A9;
    font-size: 12px;
    border: 1px solid rgba(0,0,0,0.3);
    background-color: #f5f5f5;

    th{ padding: 5px 10px; }
  }

  tbody{
    tr{
      background-color: #fff;
      border: 1px solid rgba(0,0,0,0.3);

      td{
        padding: 10px;
        text-align: center;
        font-size: 12px;
        margin-bottom: 5px;

        &.table-block{
          padding: 30px;
          font-size: 24px;
          color: #777;
          font-weight: 600;
          font-style: italic;

          .fa{
            color: #ccc;
          }
        }
      }
    }
  }
}

.status{
  text-transform: uppercase;
  font-weight: 600;

  &.verified{ color: #26D97A; }
  &.unverified{ color: #F20D0D; }
}

.cards-mobile {
  display: none;
}

@media only screen and (max-width: 430px) {
  table {
    display: none;
  }

  .respond-box {
    .card {
      width: 95%;
      max-width: fit-content;
      padding-bottom: 50px;
      height: calc(100vh - 50px);
      overflow-y: scroll;
    }
  }

  .cards-mobile {
    display: block;
    margin-top: 50px;
    .card {
      border: 1px solid #ccc;
      margin-bottom: 15px;
      padding: 8px;
      padding-bottom: 3px;
      border-bottom: 5px solid #aaa9;
      border-radius: 10px;
      display: flex;
      flex-direction: column;

      .info {
        width: 100%;
        padding-left: 10px;
      }

      > div {
        margin-bottom: 3px;
        width: 100%;
      }

      .email-logo {
        img {
          max-width: 100px;
          max-height: 100px;
        }
      }

      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
    
        .time {
          font-size: 12px;
          color: #777;
          word-spacing: 5px;
        }

        .amount {
          font-weight: 900;
          font-size: 20px;
          a {
            text-decoration: none;
          }
        }
      }

      .credit-card {
        font-weight: 900;
      }

      .title {
        display: flex;
        align-items: center;
        margin-bottom: 3px;

        &.pkg-div {
          justify-content: space-between;
        }

        .pkg-type {
          display: flex;
        }

        input {
          margin-right: 5px;
        }

        &.view {
          justify-content: space-between;
          padding-top: 7px;
          padding-bottom: 5px;
          border-top: 1px solid #eee;
        }

        .refer {
          display: flex;
        }
      }


      .label {
        font-size: 12px;
        text-transform: uppercase;
        color: #bbb;
        font-weight: 600;
        margin-right: 5px;
      }

      .value {
        margin-right: 10px;
        font-size: 13px;
        font-weight: 900;
        i {
          color: #bbb;
        }
        a {
          text-decoration: none;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Corporate/CreditCard.vue